import { useState } from "react";
import { CircleDollarSign } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { money } from "../sales/salesFormat";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import { StatusBadge } from "../../shared/ui/Status";
import { useRefundActions, useRefunds } from "./useRefunds";
export function RefundApprovalPanel() {
  const { session } = useAuth();
  const org = useOrganization(),
    query = useRefunds(),
    actions = useRefundActions();
  const [reasons, setReasons] = useState<Record<string, string>>({}),
    [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  if (!session || !org.data || !query.data) return null;
  const names = Object.fromEntries(
      org.data.accounts.map((x) => [x.id, x.name]),
    ),
    items = query.data.requests.filter(
      (x) =>
        x.status === "pending" &&
        (session.profile.role === "super" ||
          x.storeId === session.profile.storeId),
    );
  const review = async (id: string, decision: "approve" | "reject") => {
    if (
      decision === "approve" &&
      !window.confirm(
        "确认批准退款？金额与剩余课时将同步冲正，此操作不可直接撤回。",
      )
    )
      return;
    setNotice("");
    setError("");
    try {
      await actions.review({
        refundId: id,
        decision,
        reason: reasons[id] ?? "",
      });
      setNotice(
        decision === "approve"
          ? "退款已批准，课程包剩余课时已同步冲正。"
          : "退款申请已驳回。",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "审批失败");
    }
  };
  return (
    <section className="refund-panel">
      <div className="section-divider">
        <span>资金与课时一致性</span>
        <h2>退款审批</h2>
      </div>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      {items.length ? (
        <div className="approval-list">
          {items.map((x) => (
            <Card key={x.id}>
              <div className="approval-order">
                <CircleDollarSign />
                <div>
                  <span>{x.refundNumber}</span>
                  <h2>
                    {names[x.memberId]} · {money(x.refundAmountCents)}
                  </h2>
                  <p>
                    退{x.refundUnits}课时 · {x.reason}
                  </p>
                </div>
                <StatusBadge tone="warning">待审批</StatusBadge>
              </div>
              <label className="v4-field">
                <span>审批说明（驳回必填）</span>
                <input
                  value={reasons[x.id] ?? ""}
                  onChange={(e) =>
                    setReasons({ ...reasons, [x.id]: e.target.value })
                  }
                />
              </label>
              <div className="approval-actions">
                <Button
                  variant="ghost"
                  onClick={() => review(x.id, "reject")}
                  disabled={actions.pending}
                >
                  驳回
                </Button>
                <Button
                  onClick={() => review(x.id, "approve")}
                  disabled={actions.pending}
                >
                  批准退款
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="暂无退款审批"
          description="会员或员工提交退款申请后会显示在这里。"
        />
      )}
    </section>
  );
}
